import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Youtube, 
  Music, 
  Facebook, 
  Instagram, 
  MapPin, 
  Mail, 
  Phone,
  ArrowUp,
  FileText
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';

const Footer = () => {
  const [configuracion, setConfiguracion] = useState(null);
  const [redesSociales, setRedesSociales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [configResponse, redesResponse] = await Promise.all([
          apiService.getConfiguracion(),
          apiService.getRedesSociales()
        ]);

        if (configResponse.estado === 1) {
          setConfiguracion(configResponse.data);
        }

        if (redesResponse.estado === 1) {
          setRedesSociales(redesResponse.data.filter(red => red.activo === 1));
        }
      } catch (error) {
        console.error('Error cargando datos del footer:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getSocialIcon = (plataforma) => {
    const platform = plataforma.toLowerCase();
    if (platform.includes('youtube')) return Youtube;
    if (platform.includes('tiktok')) return Music;
    if (platform.includes('facebook')) return Facebook;
    if (platform.includes('instagram')) return Instagram;
    return Instagram; // Default
  };

  const navLinks = [
    { to: '/nosotros', label: 'Nosotros' },
/*     { to: '/equipamiento', label: 'Equipamiento Técnico' }, */
    { to: '/eventos', label: 'Eventos' },
    { to: '/galeria', label: 'Galería' },
    { to: '/contacto', label: 'Contacto' }
  ];

  // URL del libro de reclamaciones (puede venir de la configuración o ser una fija)
  const libroReclamacionesUrl = configuracion?.libro_reclamaciones_url 
    || configuracion?.url_libro_reclamaciones 
    || 'https://www.librodereclamaciones.com.pe/';

  if (loading) {
    return (
      <footer className="bg-black border-t border-yellow-600/20 py-12">
        <div className="container mx-auto px-4">
          <div className="animate-pulse flex space-x-4">
            <div className="h-4 bg-gray-800 rounded w-3/4"></div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-black border-t border-yellow-600/20 relative">
      <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
        {/* Grid principal */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Columna 1: Logo y descripción */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-14 h-14 rounded-full border-2 border-yellow-500 flex items-center justify-center bg-black overflow-hidden">
                  <img 
                    src="/images/logo22.png" 
                    alt="ROTMANS Logo" 
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full"></div>
              </div>
            </Link>
            
            <p className="text-gray-400 text-sm leading-relaxed">
              {configuracion?.descripcion_hero || 
                'Experiencia y tecnología en audio, luces y pantallas, acompañado de un gran equipo humano.'}
            </p>
          </motion.div>

          {/* Columna 2: Dirección y Contacto */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Dirección */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Dirección
              </h4>
              <div className="flex items-start space-x-2 text-gray-300">
                <MapPin className="w-4 h-4 text-yellow-500 mt-1 flex-shrink-0" />
                <p className="text-sm leading-relaxed">
                  {configuracion?.direccion || 'Calle Diego Ferre Nro. 375 Int. 401, Miraflores'}
                </p>
              </div>
            </div>

            {/* Contacto */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Contacto
              </h4>
              <div className="space-y-2">
                <a 
                  href={`mailto:${configuracion?.email_contacto || 'ventas@ROTMAS.com'}`}
                  className="flex items-center space-x-2 text-gray-300 hover:text-yellow-500 transition-colors group"
                >
                  <Mail className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm">{configuracion?.email_contacto || 'ventas@ROTMAS.com'}</span>
                </a>
                {configuracion?.telefono && (
                  <a 
                    href={`tel:${configuracion.telefono}`}
                    className="flex items-center space-x-2 text-gray-300 hover:text-yellow-500 transition-colors group"
                  >
                    <Phone className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm">{configuracion.telefono}</span>
                  </a>
                )}
              </div>
            </div>
          </motion.div>

          {/* Columna 3: Links de navegación */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <nav className="space-y-3">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={link.to}
                    className="text-gray-300 hover:text-yellow-500 transition-colors duration-300 text-sm block py-1"
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>

          {/* Columna 4: Redes sociales y Libro de Reclamaciones */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Redes Sociales */}
            <div className="flex space-x-3">
              {redesSociales.map((red, index) => {
                const IconComponent = getSocialIcon(red.plataforma);
                return (
                  <motion.a
                    key={red.id}
                    href={red.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-10 h-10 rounded-full border border-yellow-600/40 flex items-center justify-center text-gray-300 hover:text-yellow-500 hover:border-yellow-500 transition-all duration-300 bg-black/50"
                    aria-label={red.plataforma}
                  >
                    <IconComponent className="w-5 h-5" />
                  </motion.a>
                );
              })}
            </div>

            {/* Libro de Reclamaciones - Ahora abre en una nueva pestaña */}
            <div>
              <motion.a
                href={libroReclamacionesUrl}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ x: 4 }}
                transition={{ duration: 0.2 }}
                className="inline-flex items-center gap-2 text-xs font-semibold text-yellow-500 hover:text-yellow-400 transition-colors uppercase tracking-wider border-b border-yellow-600/30 hover:border-yellow-500 pb-1 group"
                aria-label="Abrir Libro de Reclamaciones"
              >
                <FileText className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                Libro de Reclamaciones
              </motion.a>
            </div>
          </motion.div>
        </div>

        {/* Línea divisoria */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            
            {/* Copyright */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-gray-500 text-xs text-center md:text-left"
            >
              <p>
                © {new Date().getFullYear()} {configuracion?.nombre_empresa || "ROTMAN'S Perú"}. 
                Todos los derechos reservados
              </p>
            </motion.div>

            {/* Redes sociales inferiores + Back to top */}
            <div className="flex items-center space-x-4">
              {/* Redes pequeñas */}
              <div className="flex space-x-2">
                {redesSociales.slice(0, 4).map((red) => {
                  const IconComponent = getSocialIcon(red.plataforma);
                  return (
                    <a
                      key={`footer-${red.id}`}
                      href={red.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-full border border-gray-700 flex items-center justify-center text-gray-500 hover:text-yellow-500 hover:border-yellow-600/50 transition-all duration-300"
                    >
                      <IconComponent className="w-3.5 h-3.5" />
                    </a>
                  );
                })}
              </div>

              {/* Botón Back to Top */}
              <motion.button
                onClick={scrollToTop}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="w-8 h-8 bg-yellow-600 hover:bg-yellow-500 rounded flex items-center justify-center text-black transition-colors duration-300"
                aria-label="Volver arriba"
              >
                <ArrowUp className="w-4 h-4" />
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;